import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["innerHTML"]

import { ref, computed, watch, onMounted } from 'vue';
import { Icon, loadIcon, getIcon } from '@iconify/vue';
import {
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_SIZE,
  FALLBACK_ICON_NAME,
  type IconGradientConfig,
} from '@/config/dmSuiteIconPalette';
import {
  getLinearGradientCoords,
  getRadialGradientCoords,
  getGradientStopOffsets,
  normalizeGradientConfig,
} from '@/utils/iconGradient';

interface Props {
  icon?: string | null;
  size?: number | string | null;
  styleType?: 'solid' | 'gradient' | string | null;
  color?: string | null;
  gradient?: IconGradientConfig | string | null;
  customClass?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DMSuiteIcon',
  props: {
    icon: { default: FALLBACK_ICON_NAME },
    size: { default: DEFAULT_ICON_SIZE },
    styleType: { default: 'solid' },
    color: { default: null },
    gradient: { default: null },
    customClass: { default: '' }
  },
  setup(__props: any) {

const props = __props;

let instanceSeq = 0;
const instanceId = Math.random().toString(36).substring(2, 9) + '-' + (++instanceSeq);
const gradientId = `dm-grad-${instanceId}`;

const iconName = computed(() => (props.icon || FALLBACK_ICON_NAME).trim());
const fallbackIcon = FALLBACK_ICON_NAME;

const computedSize = computed(() => {
  const num = Number(props.size);
  return Number.isFinite(num) && num > 0 ? num : DEFAULT_ICON_SIZE;
});

const isGradientMode = computed(() => {
  return props.styleType === 'gradient';
});

const normalizedGradient = computed<IconGradientConfig>(() => {
  let g = props.gradient;
  if (typeof g === 'string') {
    try {
      g = JSON.parse(g);
    } catch {
      g = null;
    }
  }
  return normalizeGradientConfig(g);
});

const linearCoords = computed(() => {
  return getLinearGradientCoords(normalizedGradient.value.angle ?? 135);
});

const radialCoords = computed(() => {
  return getRadialGradientCoords();
});

const stopOffsets = computed(() => {
  return getGradientStopOffsets(normalizedGradient.value.colors);
});

const solidStyle = computed(() => {
  if (!props.color || props.color === 'currentColor') {
    return {};
  }
  return {
    color: props.color,
  };
});

const rawBody = ref<string>('');
const viewWidth = ref<number>(24);
const viewHeight = ref<number>(24);
const iconLoaded = ref<boolean>(false);

const loadIconData = async () => {
  if (!isGradientMode.value) {
    return;
  }

  const name = iconName.value;
  if (!name) return;

  const cached = getIcon(name);
  if (cached) {
    rawBody.value = cached.body || '';
    viewWidth.value = cached.width || 24;
    viewHeight.value = cached.height || 24;
    iconLoaded.value = true;
    return;
  }

  try {
    const loaded = await loadIcon(name);
    if (loaded && iconName.value === name) {
      rawBody.value = loaded.body || '';
      viewWidth.value = loaded.width || 24;
      viewHeight.value = loaded.height || 24;
      iconLoaded.value = true;
    }
  } catch (err) {
    console.warn('[DMSuiteIcon] Failed to load icon data for gradient:', name, err);
    iconLoaded.value = false;
  }
};

const fullSvgHtml = computed(() => {
  if (!isGradientMode.value || !rawBody.value) return '';

  const stops = stopOffsets.value
    .map((s) => `<stop offset="${s.offset}" stop-color="${s.color}" />`)
    .join('');

  let gradientDef = '';
  if (normalizedGradient.value.type === 'radial') {
    const r = radialCoords.value;
    gradientDef = `<radialGradient id="${gradientId}" cx="${r.cx}" cy="${r.cy}" r="${r.r}" fx="${r.fx}" fy="${r.fy}">${stops}</radialGradient>`;
  } else {
    const l = linearCoords.value;
    gradientDef = `<linearGradient id="${gradientId}" x1="${l.x1}" y1="${l.y1}" x2="${l.x2}" y2="${l.y2}">${stops}</linearGradient>`;
  }

  const replaced = rawBody.value.replace(/currentColor/g, `url(#${gradientId})`);
  return `<svg viewBox="0 0 ${viewWidth.value} ${viewHeight.value}" width="${computedSize.value}" height="${computedSize.value}" class="dmsuite-gradient-svg ${props.customClass}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><defs>${gradientDef}</defs><g fill="url(#${gradientId})" stroke="url(#${gradientId})">${replaced}</g></svg>`;
});

watch(
  [iconName, isGradientMode],
  () => {
    loadIconData();
  },
  { immediate: true }
);

onMounted(() => {
  loadIconData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: "dmsuite-icon-root inline-flex shrink-0 items-center justify-center select-none",
    style: _normalizeStyle({ width: `${computedSize.value}px`, height: `${computedSize.value}px` })
  }, [
    (isGradientMode.value && iconLoaded.value && fullSvgHtml.value)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: "inline-flex items-center justify-center",
          innerHTML: fullSvgHtml.value
        }, null, 8, _hoisted_1))
      : (_openBlock(), _createBlock(_unref(Icon), {
          key: 1,
          icon: iconName.value || _unref(fallbackIcon),
          width: computedSize.value,
          height: computedSize.value,
          style: _normalizeStyle(solidStyle.value),
          class: _normalizeClass(['dmsuite-solid-icon', __props.customClass]),
          "aria-hidden": "true"
        }, null, 8, ["icon", "width", "height", "style", "class"]))
  ], 4))
}
}

})